import { Modal, ConfigProvider } from "antd";
import theme from "../theme.json";

type ModalProps = Parameters<typeof Modal.confirm>[0];

const configureTheme = () => {
  ConfigProvider.config({ theme: theme });
};

const createModalMethod = (modalFn: typeof Modal.confirm) => {
  return (props: Omit<ModalProps, "theme">) => {
    configureTheme();
    return modalFn(props);
  };
};

export const ThemedModal = {
  confirm: createModalMethod(Modal.confirm),
  error: createModalMethod(Modal.error),
  info: createModalMethod(Modal.info),
  warning: createModalMethod(Modal.warning),
};

/**
 * Why is ThemedModal needed?
 *
 * Modal.confirm (and Modal.info, Modal.success, etc.) are "static" methods that create
 * modals outside the normal React component tree, directly attached to the document body.
 * Because of this, they don't automatically inherit the theme context from the parent
 * ConfigProvider in App.tsx.
 *
 * Visual representation:
 * <App>
 *   <ConfigProvider theme={appTheme}>  // Theme context starts here
 *     <Components />                   // Components here get the theme
 *   </ConfigProvider>
 * </App>
 *
 * <body>
 *   <div class="ant-modal" />  // Static modal created here, outside React tree,
 *                              // doesn't get theme context automatically
 * </body>
 *
 * This wrapper ensures that all modal dialogs use our application theme by explicitly
 * setting the theme configuration before creating the modal.
 */
